import { useBrand } from '../brand/brandContext';
import joinPaths from '../path/joinPaths';
import { Brand, BrandImages, EmailBrandingImages, type BrandIcons } from '@whiz-cart/node-shared/models/brand/brand';
import { DealType } from '@whiz-cart/node-shared/models/deals/easyDeals';
import { cartImageContainerPath } from '@whiz-cart/node-shared/session/cartImagePath';
import { createContext, useCallback, useContext } from 'react';

export interface ImgSrcAsset {
    asset: string;
}
export const isImgSrcAsset = (src: ImgSrc): src is ImgSrcAsset => (src as ImgSrcAsset).asset !== undefined;

export interface ImgSrcProductImage {
    product: { itemId?: string | null; capacity?: number | null; gtin?: string | null; storeGuid?: string };
    flags?: string[] | null;
    size?: 's' | 'l' | 'm';
}
export const isImgSrcProductImage = (src: ImgSrc): src is ImgSrcProductImage => (src as ImgSrcProductImage).product !== undefined;

export interface ImgSrcFlyer {
    flyer: { relativePath?: string; pageCount?: number; extension?: string; originPath?: string };
}

export const isImgSrcFlyer = (src: ImgSrc): src is ImgSrcFlyer => (src as ImgSrcFlyer).flyer !== undefined;
export interface ImgSrcDeal {
    couponId?: string | null;
    dealType?: DealType | null;
    imageUrl?: string | null;
    itemId?: string | null;
    size: 's' | 'l' | 'm';
    /**
     * Where the deal came from. `'customerApp'` marks an EDEKA / Marktkauf app coupon, whose `imageUrl`
     * points at a third-party host rather than at anything we publish. Needed because those coupons are
     * mapped with `type: 'genussPlus'`, so the deal type alone cannot tell them apart.
     */
    source?: string | null;
}

export const isImgSrcDeal = (src: ImgSrc): src is ImgSrcDeal => !!(src as ImgSrcDeal).couponId;

export interface ImgSrcStoreMap {
    storeMap: string;
}
export const isImgSrcStoreMap = (src: ImgSrc): src is ImgSrcStoreMap => (src as ImgSrcStoreMap).storeMap !== undefined;

export interface ImgSrcRecipeImage {
    recipeImage: string;
    width: number;
    height?: number;
}
export const isImgSrcRecipeImage = (src: ImgSrc): src is ImgSrcRecipeImage => (src as ImgSrcRecipeImage).recipeImage !== undefined;

export interface ImgSrcAdvertisement {
    advertisement: string;
}
export const isImgSrcAdvertisement = (src: ImgSrc): src is ImgSrcAdvertisement => (src as ImgSrcAdvertisement).advertisement !== undefined;

export type ImgSrcBrandImage = { brandImage: keyof BrandImages | keyof BrandIcons | keyof EmailBrandingImages } | { brandImageUrl: string };
export const isImgSrcBrandImage = (src: ImgSrc): src is ImgSrcBrandImage =>
    src instanceof Object && ('brandImage' in src || 'brandImageUrl' in src);
export type ImgSrcSnapshotImage = { snapshotImageUrl: string; storeGuid: string };
export const isImgSrcSnapshotImage = (src: ImgSrc): src is ImgSrcSnapshotImage =>
    (src as ImgSrcSnapshotImage).snapshotImageUrl != undefined;

export interface ImgSrcSupportInfo {
    supportInfo: string;
}
export const isImgSrcSupportInfo = (src: ImgSrc): src is ImgSrcSupportInfo => (src as ImgSrcSupportInfo).supportInfo !== undefined;

export interface ImgSrcDigitalStamp {
    digitalStampUrl: string;
}
export const isImgSrcDigitalStamp = (src: ImgSrc): src is ImgSrcDigitalStamp => (src as ImgSrcDigitalStamp).digitalStampUrl !== undefined;

export interface ImgSrcDsiImage {
    dsiSnapshotImageUrl: string;
    storeGuid: string;
}
export const isImgSrcDsiImage = (src: ImgSrc): src is ImgSrcDsiImage => (src as ImgSrcDsiImage).dsiSnapshotImageUrl !== undefined;

// welcomeVideo
export interface ImgSrcWelcomeVideo {
    welcomeVideo: string;
}
export const isImgSrcWelcomeVideo = (src: ImgSrc): src is ImgSrcWelcomeVideo => (src as ImgSrcWelcomeVideo).welcomeVideo !== undefined;

export interface ImgSrcInfoMaterial {
    infoMaterial: string;
}
export const isImgSrcInfoMaterial = (src: ImgSrc): src is ImgSrcInfoMaterial =>
    typeof (src as ImgSrcInfoMaterial).infoMaterial !== 'undefined';

export type ImgSrc =
    | string
    | ImgSrcAsset
    | ImgSrcProductImage
    | ImgSrcStoreMap
    | ImgSrcRecipeImage
    | ImgSrcAdvertisement
    | ImgSrcFlyer
    | ImgSrcDeal
    | ImgSrcBrandImage
    | ImgSrcSnapshotImage
    | ImgSrcSupportInfo
    | ImgSrcDigitalStamp
    | ImgSrcDsiImage
    | ImgSrcPluHierarchy
    | ImgSrcWelcomeVideo
    | ImgSrcInfoMaterial
    | undefined;

export interface ImgSrcConfig {
    url?: string;
    token?: string;
    preserveAdUrl?: boolean;
    brandImageUrl?: string;
    snapshotImageUrl?: string;
    supportInfoUrl?: string;
    pluHierarchyUrl?: string;
    infoMaterialsUrl?: string;
    storeGuid?: string;
}

export interface ImgSrcPluHierarchy {
    pluHierarchyImageUrl: string;
}
export const isImgSrcPluHierarchy = (src: ImgSrc): src is ImgSrcPluHierarchy =>
    (src as ImgSrcPluHierarchy).pluHierarchyImageUrl !== undefined;

export const ImgSrcContext = createContext<ImgSrcConfig | null>(null);

export function useImgSrc() {
    const config = useContext(ImgSrcContext);
    const brand = useBrand();

    if (!config) {
        throw new Error('ImgSrcContext is not provided');
    }

    return useCallback((src: ImgSrc) => resolveImgSrc(config, brand)(src), [config, brand]);
}

export function resolveImgSrc(config: ImgSrcConfig, brand: Brand): (src: ImgSrc) => string | undefined {
    function resolve(url: string) {
        if (!config.url) return undefined;
        let result = `${config.url}/${url}`;
        if (config.token) result += `?${config.token}`;
        return result;
    }

    /**
     * The image service's external-URI proxy, for an image published somewhere the cart cannot fetch from
     * itself. Unlike `store-proxy/`, which forwards to any URL it is handed, this endpoint is bounded by a
     * configured allow-list and holds the Basic credentials that Payback's environment requires.
     */
    const resolveExternalUri = (imageUrl: string) => {
        if (!config.url) return undefined;

        // No `config.token`. The endpoint refuses any query key it does not know (`uri`, `format`,
        // `api-version`), because a stray key is how an unencoded `uri` announces itself — a token appended
        // here would be read as exactly that and answered with a 400. The cacheProxy adds whatever the CDN
        // needs on the way out.
        return `${config.url}/api/v1/externalUri?uri=${encodeURIComponent(imageUrl)}`;
    };

    const resolveProductImage = (src: ImgSrcProductImage) => {
        const { capacity, gtin } = src.product || {};
        let { itemId } = src.product || {};
        const storeGuid = src.product?.storeGuid || config.storeGuid;
        // Falsy, not just undefined: `null` interpolates as the literal segment `null` and `''` leaves an empty one, so
        // either way the request is malformed. Seen on plf-dev as `/api/v3/products/{storeGuid}/130x98`, where the image
        // service bound the size as the item ID, found no `format` and answered 400. Deals arrive here as
        // `{ product: { itemId: src.itemId } }`, and a deal's item ID is optional.
        if (!itemId) return;

        if (
            itemId.match(/-ST$/) &&
            src.flags?.map?.((s) => s.toLocaleLowerCase()).includes('isdrink') &&
            ((capacity && capacity > 1) || gtin?.length === 4) //
        ) {
            itemId = itemId.replace(/-ST$/, '-KOA');
        }

        let width = 0;
        let height = 0;

        switch (src.size) {
            case 's':
                width = 130;
                height = 98;
                break;
            case 'm':
                width = 250;
                height = 250;
                break;
            case 'l':
            default:
                width = 380;
                height = 380;
                break;
        }

        if (!storeGuid) {
            console.warn('storeGuid is missing for product image, falling back to api/v2/images');
            return resolve(`api/v2/images/${itemId}/${width}x${height}`);
        }

        return resolve(`api/v3/products/${storeGuid}/${itemId}/${width}x${height}`);
    };

    const resolveRecipeImage = (src: ImgSrcRecipeImage) => {
        const recipeImage = src.recipeImage.replace(/https:\/\/[^/]+\/+(media\/+)?/, '');
        return resolve(`api/v1/recipes/${src.width}x${src.height ?? ''}/${recipeImage}`);
    };

    const resolveAdvertisement = (src: ImgSrcAdvertisement) => {
        if (config.preserveAdUrl) return src.advertisement;
        const [, path] = src.advertisement.match(/^https?:\/\/[^/]*\/(.*)$/) || [];
        if (!path) return;
        return resolve(path);
    };

    return (src: ImgSrc) => {
        if (!src) return src;

        if (typeof src === 'string') {
            return src;
        }

        if (isImgSrcAsset(src)) {
            return resolve(src.asset);
        }

        if (isImgSrcProductImage(src)) {
            return resolveProductImage(src);
        }

        if (isImgSrcDeal(src)) {
            if (src.imageUrl) {
                // A customer-app coupon's image lives on the app backend's own host, and Payback's is behind
                // Basic auth, so `store-proxy/` cannot fetch it — it forwards without credentials. No
                // `format` is requested: these images already arrive small, and re-encoding one to 250x250
                // makes it several times larger. (150x150, which size 's' would want, is not in the
                // service's supported-format list either.)
                if (src.source === 'customerApp') {
                    return resolveExternalUri(src.imageUrl);
                }

                // TODO: should be refactored. Checking for individual deal types is not a good idea
                if (src.dealType === 'genussPlus') {
                    return resolve(`store-proxy/${src.imageUrl}`);
                } else {
                    return resolve(`api/v1/couponImage/${src.couponId}${src.size === 's' ? '/150x150' : '/250x250'}`);
                }
            }

            const s: ImgSrcProductImage = {
                product: {
                    itemId: src.itemId,
                },
                size: src.size ?? 'l',
            };
            return resolveProductImage(s);
        }

        if (isImgSrcStoreMap(src)) {
            return resolve(`api/v1/stores/${src.storeMap}`);
        }

        if (isImgSrcFlyer(src)) {
            const {
                flyer: { pageCount, extension, relativePath, originPath },
            } = src;
            if (originPath) {
                return resolve(`cdn/${originPath}`);
            }
            if (!pageCount || !extension || !relativePath || !originPath) return;
            const pageCountString = String(pageCount).padStart(2, '0');
            const url = `flyer/${relativePath}/${pageCountString}.${extension}`;
            return resolve(url);
        }

        if (isImgSrcRecipeImage(src)) {
            return resolveRecipeImage(src);
        }

        if (isImgSrcAdvertisement(src)) {
            return resolveAdvertisement(src);
        }

        if (isImgSrcBrandImage(src)) {
            let url =
                'brandImageUrl' in src
                    ? src.brandImageUrl
                    : (brand.images[src.brandImage as keyof BrandImages] ??
                      brand.icons[src.brandImage as keyof BrandIcons] ??
                      brand?.emails?.images?.[src.brandImage as keyof EmailBrandingImages] ??
                      '');

            if (!url || url.match(/^\/|blob:|data:/)) {
                return url ?? '';
            }

            url = stripPrefix(url);

            if (config.brandImageUrl) {
                return `${config.brandImageUrl}/${url}`;
            }

            return resolve(`branding/${url}`);
        }

        if (isImgSrcSnapshotImage(src)) {
            const url = src.snapshotImageUrl;
            if (src.storeGuid === undefined) {
                throw Error('storeGuid is missing for snapshot image');
            }
            return `${config.snapshotImageUrl}/camera-images/${cartImageContainerPath(src.storeGuid, url)}`;
        }

        if (isImgSrcSupportInfo(src)) {
            return `${config.supportInfoUrl}/${src.supportInfo}`;
        }

        if (isImgSrcDigitalStamp(src)) {
            return resolve(`digitalStamp/${src.digitalStampUrl}`);
        }

        if (isImgSrcDsiImage(src)) {
            const url = src.dsiSnapshotImageUrl;
            if (src.storeGuid === undefined) {
                throw Error('storeGuid is missing for dsi image');
            }
            return resolve(`dsiImage/${cartImageContainerPath(src.storeGuid, url)}`);
        }

        if (isImgSrcPluHierarchy(src)) {
            if (config.pluHierarchyUrl) {
                return `${config.pluHierarchyUrl}/${src.pluHierarchyImageUrl}`;
            }
            return resolve(`cdn/plu-hierarchies/${src.pluHierarchyImageUrl}`);
        }

        if (isImgSrcWelcomeVideo(src)) {
            return resolve(`cdn/login-videos/${src.welcomeVideo}`);
        }

        if (isImgSrcInfoMaterial(src)) {
            if (config.infoMaterialsUrl) {
                return joinPaths(config.infoMaterialsUrl, src.infoMaterial);
            }

            return resolve(`cdn/${src.infoMaterial}`);
        }

        throw Error(`Unknown src type: ${JSON.stringify(src)}`);
    };
}

function stripPrefix(url: string) {
    return url.replace(/https:\/\/[^/]+\//, '');
}
